import CircleByIdButton from '@/circle/components/CircleByIdButton'
import useDateLocale from '@/common/hooks/useDateLocale'
import MemberAvatar from '@/member/components/MemberAvatar'
import MemberLink from '@/member/components/MemberLink'
import { useOrgContext } from '@/org/contexts/OrgContext'
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Icon,
  LinkBoxProps,
  Text,
} from '@chakra-ui/react'
import { capitalizeFirstLetter } from '@utils/capitalizeFirstLetter'
import { format } from 'date-fns'
import React, { ReactNode, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'

interface Props extends LinkBoxProps {
  // The header sentence: an i18n key with an <author /> slot, or a ready-made
  // sentence (a governance change already says who did what, ACTU-01)
  i18nKey?: string
  sentence?: ReactNode
  date: string
  memberId?: string
  circleId?: string
  icon?: IconType
  // A small action next to the role tag (the detail of a change, #114)
  action?: ReactNode
  children?: ReactNode
}

export default function NewsItemLayout({
  i18nKey,
  sentence,
  date: dateStr,
  memberId,
  circleId,
  icon,
  action,
  children,
}: Props) {
  const { t } = useTranslation()
  const dateLocale = useDateLocale()
  const date = useMemo(() => new Date(dateStr), [dateStr])
  const { orgData } = useOrgContext()
  const member = orgData?.getMember(memberId)

  return (
    <Card boxShadow="none">
      <CardHeader display="flex" pb={0}>
        {icon ? (
          <Icon as={icon} w="2em" h="2em" />
        ) : (
          member && <MemberAvatar member={member} noTooltip w="2em" h="2em" />
        )}

        <Box flex="1" ml={3} mr={4}>
          <Text>
            {sentence ?? (
              <Trans
                i18nKey={i18nKey as any}
                components={{
                  author: member ? (
                    <MemberLink
                      id={member.id}
                      name={member.name}
                      fontWeight="500"
                    />
                  ) : (
                    <>…</>
                  ),
                }}
              />
            )}
          </Text>
          <Text fontSize="xs" fontWeight="normal" color="gray.400">
            {capitalizeFirstLetter(
              format(date, 'PPPP', {
                locale: dateLocale,
              })
            )}
            {t('common.dates.dateHourSeparator')}
            {format(date, 'HH:mm', {
              locale: dateLocale,
            })}
          </Text>
        </Box>

        {(action || circleId) && (
          <HStack spacing={2} align="flex-start">
            {action}
            {circleId && <CircleByIdButton id={circleId} size="xs" />}
          </HStack>
        )}
      </CardHeader>

      {children ? <CardBody p={4}>{children}</CardBody> : <Box pb={5} />}
    </Card>
  )
}
